import { fetchStores } from 'api/Stores/actions'
import classNames from 'classnames'
import { selectChannelsType, selectIsExpanded, selectTabRequestParams } from 'data/selectors'
import type { DetailsData } from 'data/types'
import { getExtendType, onExpand } from 'expanded/actions'
import type { Expanded } from 'expanded/types'
import { getDefaultContentType } from 'GroupDetails/utils'
import { LoadingOverlay } from 'LoadingOverlay'
import type { ReactNode, SyntheticEvent } from 'react'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Carousel } from 'react-responsive-carousel'
import { SliderDots } from 'SliderDots'
import { selectLastReFetchTime } from 'store/reducers/lastReFetchTime'
import type { State } from 'store/types'
import { noop } from 'utils'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'

import { DetailsType } from './constants'
import DetailsBudget from './DetailsBudget'
import DetailsDefault from './DetailsDefault'
import DetailsECom from './DetailsECom'
import DetailsL4L from './DetailsL4L'
import DetailsMain from './DetailsMain'
import DetailsRetail from './DetailsRetail'
import type { ChannelSummaryType } from './types'
import useStyles from './useStyles'

export interface ChannelSummaryProps {
  channel?: string
  children: ReactNode
  className?: string
  countryId?: string
  data: DetailsData
  detailsType?: DetailsType
  fetchList?: () => void
  hideFlag?: boolean
  isLoading?: boolean
  label: string
  onExpand?: () => void
  owner?: string
  saleType?: string
  type: ChannelSummaryType
}

const transitionProps = { unmountOnExit: true }

function ChannelSummary({
  className,
  channel,
  children = null,
  countryId,
  data,
  detailsType,
  fetchList = noop,
  onExpand: onExpandAction = noop,
  hideFlag = false,
  isLoading = false,
  label,
  owner,
  saleType,
  type,
}: ChannelSummaryProps) {
  const dispatch = useDispatch()
  const classes = useStyles()
  const channelsType = useSelector(selectChannelsType)
  const tabRequestParams = useSelector(selectTabRequestParams)
  const isListDetailsOpen = useSelector((state: State) => state.isListDetailsOpen)
  const countryType = getExtendType({ owner, saleType })
  const expandType: keyof Expanded = type === 'countries' && channelsType !== 'countries' ? `countries-${countryType}` : type
  const expandedType = useSelector((state: State) => state.expanded[expandType])
  const shouldHideSummary = type === 'countries' ? expandedType !== countryId : expandedType !== label
  const lastReFetchTime = useSelector(selectLastReFetchTime)
  const isToggled = useSelector(selectIsExpanded({ channel, countryId, expandType, owner, saleType, type }))
  const initialContentType = getDefaultContentType({ data, type })
  const fetchStoresAction = useCallback(() => {
    dispatch(
      fetchStores({
        ...tabRequestParams,
        isListDetailsOpen,
        channel,
        countryId,
        owner,
        saleType,
        storesType: initialContentType,
        type,
      }),
    )
  }, [dispatch, tabRequestParams, isListDetailsOpen, channel, countryId, owner, saleType, initialContentType, type])

  const onChange = useCallback(
    (_event: React.SyntheticEvent, expanded: boolean) => {
      if (expanded) {
        fetchStoresAction()
        onExpandAction()
      }

      dispatch(onExpand({ channelsType, countryId, expanded, owner, saleType, type, label }))
    },
    [dispatch, channelsType, countryId, owner, saleType, type, label, fetchStoresAction, onExpandAction],
  )
  const classesAccordionSummary = {
    content: classes.accordionSummaryContent,
    expanded: classes.accordionSummaryExpanded,
    root: classes.accordionSummaryRoot,
  }

  // Refetch lists
  useEffect(() => {
    if (isToggled && (channelsType === type || type === 'group')) fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToggled, channelsType, lastReFetchTime, type])
  const supportedDetails = [
    Boolean(data.storesCurrentL4L || data.storesComparedL4L || !data.currency) && DetailsType.L4L,
    DetailsType.DEFAULT,
    DetailsType.BUDGET,
    Boolean(data.storesCurrentNECom || data.storesComparedNECom || !data.currency) && DetailsType.RETAIL,
    Boolean(data.storesCurrentECom || data.storesComparedECom || !data.currency) && DetailsType.ECOM,
  ].filter(detailsType => typeof detailsType === 'number') as DetailsType[]

  const [currentSlide, setCurrentSlide] = useState(supportedDetails.indexOf(detailsType || DetailsType.DEFAULT))
  const onPrevious = (event: SyntheticEvent) => {
    event.stopPropagation()

    if (currentSlide > 0) setCurrentSlide(currentSlide - 1)
  }
  const onNext = (event: SyntheticEvent) => {
    event.stopPropagation()

    if (currentSlide < supportedDetails.length) setCurrentSlide(currentSlide + 1)
  }
  const isSubList = !Array.isArray(children) && type !== 'group'

  return (
    <Box display={expandedType && shouldHideSummary ? 'none' : 'block'} className={classNames(classes.root, className)}>
      <Accordion
        aria-label={`${type} summary`}
        role="treeitem"
        classes={{ root: classes.accordion, disabled: classes.disabled }}
        expanded={isToggled}
        disabled={isLoading}
        onChange={onChange}
        TransitionProps={transitionProps}
      >
        <AccordionSummary classes={classesAccordionSummary}>
          <Box position="relative">
            <Box
              className={classNames(classes.swipeFirst, {
                [classes.subList]: isSubList,
                [classes.swipeFirstInactive]: currentSlide === 0,
              })}
              onClick={onPrevious}
            />
            <Box
              className={classNames(classes.swipeLast, {
                [classes.subList]: isSubList,
                [classes.swipeLastInactive]: currentSlide >= supportedDetails.length - 1,
              })}
              onClick={onNext}
            />
            <Box className="carousel-wrapper">
              <Carousel
                dynamicHeight
                emulateTouch
                onChange={(index: number) => setCurrentSlide(index)}
                selectedItem={currentSlide}
                showArrows={false}
                showStatus={false}
                showThumbs={false}
                swipeScrollTolerance={70}
              >
                {supportedDetails.map((key, index) => (
                  <Box key={key} width="100%" position="relative">
                    <LoadingOverlay isLoading={isLoading}>
                      <SliderDots
                        className={classes.sliderDots}
                        length={supportedDetails.length}
                        selected={index}
                        onChange={setCurrentSlide}
                      />
                      <i className={classNames('fm fm-dropdownlist rotatable', classes.dropdownIcon, { rotate: isToggled })} />
                      <Grid container>
                        <DetailsMain countryId={countryId} data={data} detailsType={key} hideFlag={hideFlag} label={label} />
                        {key === DetailsType.DEFAULT && <DetailsDefault data={data} />}
                        {key === DetailsType.L4L && <DetailsL4L data={data} />}
                        {key === DetailsType.RETAIL && <DetailsRetail data={data} />}
                        {key === DetailsType.ECOM && <DetailsECom data={data} />}
                        {key === DetailsType.BUDGET && <DetailsBudget data={data} />}
                      </Grid>
                    </LoadingOverlay>
                  </Box>
                ))}
              </Carousel>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails classes={{ root: classes.accordionDetails }}>{children}</AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default memo<ChannelSummaryProps>(ChannelSummary)
